import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import { verifyPromoCode } from '../../services/flightBookingAPI';

const PromoCodeList = ({ fareQuoteData, userToken, rcBookingId, appliedPromo, setAppliedPromo }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalState, setModalState] = useState('loading'); // 'loading' | 'success' | 'invalid'
  const [windowDimension, setWindowDimension] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Process data from props
  const validPromoCodesObj = fareQuoteData?.valid_promo_codes || {};

  useEffect(() => {
    const handleResize = () => {
      setWindowDimension({ width: window.innerWidth, height: window.innerHeight });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dynamicPromos = Object.entries(validPromoCodesObj).map(([code, details]) => ({
    code,
    description: details.desc || '',
    amount: details.amount || 0,
  }));

  const handleApply = async (promo) => {
    setModalState('loading');
    setIsModalOpen(true);

    try {
      const result = await verifyPromoCode(rcBookingId, promo.code, promo.amount, userToken);

      if (result?.data?.is_valid === false) {
        setModalState('invalid');
      } else {
        if (setAppliedPromo) {
          setAppliedPromo({ code: promo.code, amount: promo.amount, description: promo.description });
        }
        setModalState('success');
      }
    } catch (error) {
      console.error(error);
      setIsModalOpen(false);
      alert('Failed to apply promo code: ' + (error.message || 'Unknown error'));
    }
  };

  const handleRemove = () => {
    if (setAppliedPromo) {
      setAppliedPromo({ code: '', amount: 0, description: '' });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      setModalState('loading');
    }, 300);
  };

  if (dynamicPromos.length === 0) {
    return null; // Don't show if no promos available
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-6 mt-6 border border-gray-100 w-full">
        <h4 className="text-[18px] font-semibold text-[#2d3e50] mb-4">Offers & Promo Codes</h4>

        {/* Promo List */}
        <div className="space-y-4 max-h-[350px] overflow-y-auto pr-1">
          {dynamicPromos.map((promo, idx) => {
            const isApplied = appliedPromo?.code === promo.code;

            return (
              <div key={idx} className={`border border-dashed rounded-lg p-4 transition-all duration-300 group ${isApplied ? 'bg-[#f0fdf4] border-[#22c55e]' : 'bg-[#fcfdff] border-gray-300 hover:bg-[#fff9f6] hover:border-[#FF5B15]'}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`font-bold px-3 py-1 rounded border text-[13px] uppercase tracking-wider transition-colors ${isApplied ? 'bg-[#dcfce7] text-[#15803d] border-[#bbf7d0]' : 'bg-[#fff7ed] text-[#ea580c] border-[#fed7aa] group-hover:bg-[#FF5B15] group-hover:text-white'}`}>
                    {promo.code}
                  </span>

                  {isApplied ? (
                    <div className="flex items-center gap-3">
                      <span className="text-[#15803d] font-bold text-[14px]">Applied</span>
                      <button
                        onClick={handleRemove}
                        className="text-red-500 font-semibold text-[13px] hover:underline cursor-pointer">
                        Remove
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleApply(promo)}
                      className="text-[#FF5B15] font-bold text-[14px] hover:underline cursor-pointer">
                      Apply
                    </button>
                  )}
                </div>

                <p className="text-gray-600 text-[14px] mt-2 leading-snug">
                  {promo.description}
                </p>
                <div className="mt-2 text-[13px] text-gray-500 font-medium flex items-center gap-1.5">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Save <span className="text-green-600 font-bold">₹{promo.amount}</span> on this booking
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          {modalState === 'success' && (
            <Confetti
              width={windowDimension.width}
              height={windowDimension.height}
              recycle={false}
              numberOfPieces={300}
              gravity={0.6}
              friction={0.96}
              initialVelocityX={45}
              initialVelocityY={60}
              tweenDuration={50}
              colors={['#FF5B15', '#10B981', '#3B82F6', '#F59E0B']}
              confettiSource={{
                x: windowDimension.width / 2,
                y: windowDimension.height / 2.5,
                w: 0,
                h: 0
              }}
              style={{ position: 'fixed', top: 0, left: 0, zIndex: 105, pointerEvents: 'none' }}
            />
          )}

          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full mx-4 relative z-[102] text-center transform transition-all">
            {modalState === 'loading' ? (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 border-4 border-gray-100 border-t-[#FF5B15] rounded-full animate-spin mb-6"></div>
                <h3 className="text-xl font-bold text-[#2d3e50] mb-2">Applying Promo Code</h3>
                <p className="text-gray-500 text-sm font-medium">Please wait while we apply the promo code to your booking...</p>
              </div>
            ) : modalState === 'invalid' ? (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#2d3e50] mb-2">Invalid Promo Code</h3>
                <p className="text-gray-600 text-base mb-6">
                  The promo code you entered is not valid for this booking.
                </p>
                <button
                  onClick={closeModal}
                  className="w-full bg-gray-200 text-gray-800 py-3 rounded-xl font-bold text-base hover:bg-gray-300 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#2d3e50] mb-2">Awesome!</h3>
                <p className="text-gray-600 text-base mb-6">
                  Promo code <span className="font-bold text-[#FF5B15]">{appliedPromo?.code}</span> has been successfully applied to your booking.
                </p>
                <button
                  onClick={closeModal}
                  className="w-full bg-[#FF5B15] text-white py-3 rounded-xl font-bold text-base hover:bg-[#e04e12] transition-colors shadow-lg shadow-orange-500/30"
                >
                  Continue
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default PromoCodeList;
